import Modal from '@components/v2/ui/Modal/Modal';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import { Button, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import ButtonWithPopover from '../../../components/ButtonWithPopover';
import ConnectModal from '../../../components/Modal/ConnectModal';
import StoreNotVerifiedModal from '../../../components/Modal/StoreNotVerifiedModal';
import useHandleAppException, { AppExceptionResponse } from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import snackbarStore from '../../../stores/Snackbar.store';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { eventRepository } from '../../events/event.repository';
import { useAuthContext } from '../../login/AuthContext';
import ConnectAccountModal from '../../login/ConnectAccountModal';
import { useTranslation } from '../../translation/TranslationContext';
import EditSalesRepModal from '../EditSalesRepModal';
import WithoutLicenseModal from '../WithoutLicenseModal';
import AcceptConnectionModal from '../explore/AcceptConnectionModal';
import SuggestionModal from '../explore/SuggestionModal';
import { myNetworkExploreRepository } from '../explore/myNetworkExplore.repository';
import { Explore } from '../explore/myNetworkExplore.types';
import { myNetworkInvitesReceivedRepository } from '../invites-received/myNetworkInvitesReceived.repository';
import { myNetworkInvitesSentRepository } from '../invites-sent/myNetworkInvitesSent.repository';
import { myNetworkInvitesSuggestionRepository } from '../invites-suggestions/myNetworkInvitesSuggestion.repository';
import { isRefusedByBrand, isRefusedByStore } from '../myNetworkUtils';
import { myNetworkConnectionsRepository } from './MyNetworkConnections.repository';
import { createFailStoreConnectionLog } from './connections.repository';

const whiteBackgroundStyle = {
  backgroundColor: 'white',
};

enum ModalEnum {
  CONNECT = 'CONNECT',
  EDIT_SALES_REP = 'EDIT_SALES_REP',
  SUGGESTION = 'SUGGESTION',
  ACCEPT = 'ACCEPT',
  WITHOUT_LICENSES = 'WITHOUT_LICENSES',
  STORE_NOT_VERIFIED = 'STORE_NOT_VERIFIED',
  LOGIN = 'LOGIN',
  CONFIRM_REJECT = 'CONFIRM_REJECT',
}
type ButtonVariant = 'text' | 'outlined' | 'contained';
type ConnectionActionButtonProps = {
  connection: Explore;

  onChangeConnection: () => void;
  acceptUnverifiedStoreInvite?: boolean;
  connectButtonVariant?: ButtonVariant;
  size?: 'small' | 'medium' | 'large';
  shouldVerifyStorefront?: boolean;
  brandSlug?: string;
};

interface HandleRejectOptions {
  reject: (inviteId: string) => Promise<AppExceptionResponse>;
  successMessage: LangEnum;
  errorMessage: LangEnum;
}

interface RemoveConnectionOptions {
  connectionId: string;
  brandId: string;
}

type RejectTarget = 'INVITE' | 'SUGGESTION';

interface RejectConfig extends HandleRejectOptions {
  title: LangEnum;
  message: LangEnum;
}

const REJECT_CONFIG: Record<RejectTarget, RejectConfig> = {
  INVITE: {
    reject: (inviteId) => myNetworkInvitesReceivedRepository().ignore(inviteId),
    title: LangEnum.MY_NETWORK_RECEIVED_LIST_CONFIRM_TITLE,
    message: LangEnum.MY_NETWORK_RECEIVED_LIST_CONFIRM_MESSAGE,
    successMessage: LangEnum.MY_NETWORK_RECEIVED_LIST_RECUSED_SUCCESS,
    errorMessage: LangEnum.MY_NETWORK_RECEIVED_LIST_ERROR,
  },
  SUGGESTION: {
    reject: (inviteId) => myNetworkInvitesSuggestionRepository().reject(inviteId),
    title: LangEnum.MY_NETWORK_SUGGESTIONS_LIST_REJECT_CONFIRMATION_TITLE,
    message: LangEnum.MY_NETWORK_SUGGESTIONS_LIST_REJECT_CONFIRMATION_MESSAGE,
    successMessage: LangEnum.MY_NETWORK_SUGGESTIONS_LIST_REJECT_SUCCESS,
    errorMessage: LangEnum.MY_NETWORK_SUGGESTIONS_LIST_REMOVE_ERROR,
  },
};

const ConnectionActionButton = ({
  acceptUnverifiedStoreInvite,
  connection,
  brandSlug,
  onChangeConnection,
  connectButtonVariant = 'outlined',
  size = 'medium',
  shouldVerifyStorefront = true,
}: ConnectionActionButtonProps) => {
  const { loggedin, handleSetLogin } = useAuthContext();
  const [selectedModal, setSelectedModal] = useState<ModalEnum>();
  const [messageValue, setMessageValue] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isRejecting, setIsRejecting] = useState<boolean>(false);
  const [rejectTarget, setRejectTarget] = useState<RejectTarget>();
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;
  const { handleErrorResponse } = useHandleAppException();

  const { state, isSeller, isAdminBrand, isAdminStore, isStore, loadBrands, removeSelectedBrand } =
    useApplicationContext();

  const isAdmin = isAdminBrand || isAdminStore;
  const status = isStore ? connection.invite?.storeStatus : connection.invite?.brandStatus;

  const isSuggestedSeller =
    isStore &&
    connection.invite?.brandStatus === 'SUGERIDO_VENDEDOR' &&
    !connection.invite?.storeStatus;

  const isRejectedAdmin =
    isStore &&
    connection.invite?.brandStatus === 'RECUSADO_ADMIN' &&
    !connection.invite?.storeStatus;

  const isSuggestionDeleted = !isStore && status === 'RECUSADO_ADMIN';

  const hasInviteRefusedByStore = !!connection.invite && isRefusedByStore(connection.invite);

  const hasInviteRefusedByBrand = !!connection.invite && isRefusedByBrand(connection.invite);

  const isRejectedByStore = !isStore && hasInviteRefusedByStore;

  const isRejectedByBrand = isStore && hasInviteRefusedByBrand;

  const canUndoOwnRefusal = isAdminStore && hasInviteRefusedByStore;

  const showConnectionButton =
    (!connection.invite ||
      isSuggestionDeleted ||
      isSuggestedSeller ||
      isRejectedByStore ||
      isRejectedByBrand) &&
    !connection.connection;

  useEffect(() => {
    if (connection) {
      setMessageValue(connection.invite?.message);
    }
  }, [connection]);

  const handleOpenConnectModal = async () => {
    if (!loggedin) {
      connection.brand &&
        handleSetLogin({
          id: connection.brand.id,
          slug: connection.brand.slug,
          bannerUrl: connection.brand.bannerUrl || '',
          enablePublicShowroom: connection.brand.enablePublicShowroom,
          brandName: connection.brand.name,
          customerSupportPhone: connection.brand.customerSupportPhone,
        });

      setSelectedModal(ModalEnum.LOGIN);
      return;
    }
    const repository = eventRepository('');

    let shouldOpenStoreNotVerified: boolean | undefined = false;

    if (isStore) {
      const response = await repository.checkIfBrandAreInSameEvent(connection.brand?.id);
      const storeAndBrandAreInSameEvent = response.ok && response.data?.storeAndBrandAreInSameEvent;

      shouldOpenStoreNotVerified =
        shouldVerifyStorefront && !state.user?.store?.verified && !storeAndBrandAreInSameEvent;
    }

    if (shouldOpenStoreNotVerified && state.user && state.user?.store) {
      await createFailStoreConnectionLog({
        userId: state.user?.userId,
        brandId: connection.id,
        storeId: state.user?.store?.id,
      });
    }

    setSelectedModal(shouldOpenStoreNotVerified ? ModalEnum.STORE_NOT_VERIFIED : ModalEnum.CONNECT);
  };

  const handleOpenEditSalesRepModal = () => {
    setSelectedModal(ModalEnum.EDIT_SALES_REP);
  };

  const handleOpenAcceptModal = () => {
    setSelectedModal(ModalEnum.ACCEPT);
  };
  const handleCloseAcceptModal = () => {
    setSelectedModal(undefined);
  };

  const handleOpenSuggestionModal = () => {
    setSelectedModal(ModalEnum.SUGGESTION);
  };
  const handleCloseSuggestionModal = () => {
    setMessageValue(undefined);
    setSelectedModal(undefined);
  };

  const handleCloseEditSalesRepModal = () => {
    setSelectedModal(undefined);
  };

  const handleOpenWithoutLicenseModal = (message: string) => {
    setMessageValue(message);
    setSelectedModal(ModalEnum.WITHOUT_LICENSES);
  };

  const handleWithoutLicenseErrorMessage = (message: string) => {
    if (!isAdminBrand) {
      handleMessage({
        type: 'warning',
        message: translate(LangEnum.MY_NETWORK_RECEIVED_LIST_NO_LICENSES),
      });
      return;
    }

    handleOpenWithoutLicenseModal(message);
  };

  const handleConnect = async (message: any, sellers: any, destinationId: any) => {
    let response: any;
    setIsSubmitting(true);
    if (isStore) {
      response = await myNetworkExploreRepository().connect({
        message,
        destinationId,
        acceptUnverified: acceptUnverifiedStoreInvite,
      });
    } else {
      response = await myNetworkExploreRepository().connect({
        message,
        destinationId,
        sellers,
        acceptUnverified: acceptUnverifiedStoreInvite,
      });
    }
    setIsSubmitting(false);
    if (response.ok) {
      onChangeConnection();
      setSelectedModal(undefined);
      handleMessage({
        type: 'success',
        message: translate(LangEnum.MY_NETWORK_EXPLORE_INVITE_SUCCESS),
      });
      return;
    }

    if (response.status === 400) {
      return handleWithoutLicenseErrorMessage(response.data.message);
    }

    handleMessage({ type: 'error', message: translate(LangEnum.MY_NETWORK_EXPLORE_INVITE_ERROR) });
  };

  const handleDeleteSuggestion = async (inviteSuggested: any) => {
    const { invite } = inviteSuggested;

    const response = await myNetworkInvitesSuggestionRepository().remove(invite.id);

    if (response.ok) {
      onChangeConnection();
      handleMessage({
        type: 'success',
        message: translate(LangEnum.MY_NETWORK_SUGGESTIONS_LIST_REMOVE_SUCCESS),
      });
      return;
    }
    handleMessage({
      type: 'error',
      message: translate(LangEnum.MY_NETWORK_SUGGESTIONS_LIST_REMOVE_ERROR),
    });
  };

  const handleAcceptSugestion = async (inviteSuggested: any) => {
    const { invite } = inviteSuggested;

    const repository = myNetworkInvitesSuggestionRepository();

    if (messageValue) {
      const messageResponse = await repository.updateMessage(invite.id, messageValue);
      if (!messageResponse.ok) {
        handleMessage({
          type: 'error',
          message: translate(LangEnum.MY_NETWORK_EXPLORE_MESSAGE_UPDATED),
        });
        return;
      }
      handleMessage({
        type: 'info',
        message: translate(LangEnum.MY_NETWORK_SUGGESTIONS_LIST_UPDATE_MESSAGE_SUCCESS),
      });
    }

    const response = await repository.connect(invite.id);

    if (response.ok) {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.MY_NETWORK_RECEIVED_LIST_ACCEPT_SUCCESS),
      });
      onChangeConnection();
      return;
    }

    if (response.status === 400) {
      return handleWithoutLicenseErrorMessage(response.data.message);
    }

    handleMessage({ type: 'error', message: translate(LangEnum.MY_NETWORK_RECEIVED_LIST_ERROR) });
  };

  const handleReject = async ({ reject, successMessage, errorMessage }: HandleRejectOptions) => {
    const inviteId = connection.invite?.id;
    if (!inviteId) {
      setSelectedModal(undefined);
      return;
    }
    setIsRejecting(true);
    const response = await reject(inviteId);
    setIsRejecting(false);
    setSelectedModal(undefined);
    if (response.ok) {
      onChangeConnection();
      handleMessage({ type: 'success', message: translate(successMessage) });
      return;
    }
    if (response.data?.code) {
      handleMessage({ type: 'error', message: handleErrorResponse(response) });
      return;
    }
    handleMessage({ type: 'error', message: translate(errorMessage) });
  };

  const handleAskReject = (target: RejectTarget) => {
    setRejectTarget(target);
    setSelectedModal(ModalEnum.CONFIRM_REJECT);
  };

  const rejectConfig = rejectTarget ? REJECT_CONFIG[rejectTarget] : undefined;

  const handleAcceptInvite = async () => {
    const response = await myNetworkInvitesReceivedRepository().accept(connection.invite?.id);

    if (response.ok) {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.MY_NETWORK_RECEIVED_LIST_ACCEPT_SUCCESS),
      });
      onChangeConnection();
      loadBrands();
      handleCloseAcceptModal();

      if (state.brands.length < 1) {
        onChangeConnection();
      }

      return;
    }

    if (response.data.code) {
      const errorMessage = handleErrorResponse(response);
      handleMessage({ type: 'error', message: errorMessage });
      return;
    }

    if (response.status === 400) {
      return handleWithoutLicenseErrorMessage(response.data.message);
    }

    handleMessage({ type: 'error', message: translate(LangEnum.MY_NETWORK_RECEIVED_LIST_ERROR) });
  };

  const handleRemoveInvite = async (inviteSent: any) => {
    const { invite } = inviteSent;

    const response = await myNetworkInvitesSentRepository().remove(invite.id);
    if (response.ok) {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.MY_NETWORK_SENT_LIST_REMOVE_INVITATION_SUCCESS),
      });
      onChangeConnection();
      return;
    }
    handleMessage({
      type: 'error',
      message: translate(LangEnum.MY_NETWORK_SENT_LIST_REMOVE_INVITATION_ERROR),
    });
  };

  const handleRemoveConnection = async ({ connectionId, brandId }: RemoveConnectionOptions) => {
    const response = await myNetworkConnectionsRepository(connectionId).remove();
    if (!response.ok) {
      handleMessage({ type: 'error', message: translate(LangEnum.MY_NETWORK_REMOVE_ERROR) });
      return;
    }
    if (brandId === state.brand?.id) {
      removeSelectedBrand();
    }
    onChangeConnection();
    handleMessage({ type: 'success', message: translate(LangEnum.MY_NETWORK_REMOVE_SUCCESS) });
  };

  return (
    <>
      <ConnectModal
        open={selectedModal === ModalEnum.CONNECT}
        brandOrStoreName={connection.store?.name || connection.brand?.name}
        brandOrStoreId={connection.store?.id || connection.brand?.id}
        onClose={() => setSelectedModal(undefined)}
        onConnect={(message, sellers, destinationId) =>
          handleConnect(message, sellers, destinationId)
        }
        isSubmitting={isSubmitting}
      />
      {!loggedin && (
        <ConnectAccountModal
          open={selectedModal === ModalEnum.LOGIN}
          onClose={() => setSelectedModal(undefined)}
          brandSlug={brandSlug}
        />
      )}
      {isStore && (
        <StoreNotVerifiedModal
          open={selectedModal === ModalEnum.STORE_NOT_VERIFIED}
          onClose={() => setSelectedModal(undefined)}
        />
      )}
      {isAdmin && (
        <AcceptConnectionModal
          open={selectedModal === ModalEnum.ACCEPT}
          onClose={handleCloseAcceptModal}
          brandOrStoreName={connection.store?.name || connection.brand?.name}
          connection={connection}
          onAccept={handleAcceptInvite}
        />
      )}
      {rejectConfig && (
        <Modal
          isOpen={selectedModal === ModalEnum.CONFIRM_REJECT}
          size="standard"
          header={{ title: rejectConfig.title }}
          onClose={() => setSelectedModal(undefined)}
          footer={{
            primaryAction: {
              label: LangEnum.MY_NETWORK_SUGGESTIONS_LIST_DECLINE,
              isLoading: isRejecting,
              onClick: () => handleReject(rejectConfig),
            },
            secondaryAction: {
              label: LangEnum.GLOBAL_CANCEL_LABEL,
              onClick: () => setSelectedModal(undefined),
            },
          }}
        >
          {translate(rejectConfig.message, {
            brandOrStoreName: connection.store?.name || connection.brand?.name,
          })}
        </Modal>
      )}
      {isAdminBrand && (
        <>
          <EditSalesRepModal
            open={selectedModal === ModalEnum.EDIT_SALES_REP}
            reload={onChangeConnection}
            item={connection.invite}
            isConnection={false}
            onClose={handleCloseEditSalesRepModal}
          />
          <SuggestionModal
            open={selectedModal === ModalEnum.SUGGESTION}
            onClose={handleCloseSuggestionModal}
            brandOrStoreName={connection.store?.name || connection.brand?.name}
            connection={connection}
            messageValue={messageValue}
            isMessageEditable={isAdminBrand}
            onChangeMessage={setMessageValue}
            onAccept={() => handleAcceptSugestion(connection)}
          />
          <WithoutLicenseModal
            open={selectedModal === ModalEnum.WITHOUT_LICENSES}
            onClose={() => setSelectedModal(undefined)}
            onExited={() => setMessageValue(undefined)}
            message={messageValue}
            onClickExpand={() =>
              handleMessage({
                type: 'info',
                message: translate(LangEnum.MY_NETWORK_RECEIVED_LIST_UNDER_DEVELOPMENT),
              })
            }
          />
        </>
      )}
      <>
        {showConnectionButton && (
          <Button
            size={size}
            variant={connectButtonVariant}
            color="primary"
            onClick={() => handleOpenConnectModal()}
            fullWidth
          >
            {translate(LangEnum.MY_NETWORK_SUGGESTIONS_LIST_CONNECT)}
          </Button>
        )}

        {status === 'SOLICITADO' && !isRejectedByStore && !isRejectedByBrand && (
          <ButtonWithPopover
            label={translate(LangEnum.MY_NETWORK_EXPLORE_REQUESTED)}
            fullWidth
            color="default"
          >
            <Button
              variant="text"
              color="secondary"
              startIcon={<Close />}
              onClick={() => handleRemoveInvite(connection)}
            >
              {translate(LangEnum.MY_NETWORK_EXPLORE_REMOVE_INVITATION)}
            </Button>
          </ButtonWithPopover>
        )}

        {/* admin loja */}
        {isStore && status === 'AGUARDANDO' && (
          <>
            {isAdminStore ? (
              <ButtonWithPopover label={translate(LangEnum.MY_NETWORK_EXPLORE_ANSWER)} fullWidth>
                <Grid container direction="column">
                  <Grid size={12}>
                    <Button
                      fullWidth
                      variant="text"
                      color="secondary"
                      startIcon={<Check />}
                      onClick={handleOpenAcceptModal}
                    >
                      {translate(LangEnum.MY_NETWORK_RECEIVED_LIST_ACCEPT)}
                    </Button>
                  </Grid>
                </Grid>
              </ButtonWithPopover>
            ) : (
              <Button variant="outlined" disabled fullWidth style={whiteBackgroundStyle}>
                {translate(LangEnum.MY_NETWORK_EXPLORE_RECEIVED)}
              </Button>
            )}
          </>
        )}
        {/* admin marca */}
        {!isStore && status === 'AGUARDANDO' && (
          <>
            {isAdminBrand ? (
              <ButtonWithPopover label={translate(LangEnum.MY_NETWORK_EXPLORE_ANSWER)} fullWidth>
                <Grid container direction="column">
                  <Grid size={12}>
                    <Button
                      fullWidth
                      variant="text"
                      color="secondary"
                      startIcon={<Check />}
                      onClick={handleOpenAcceptModal}
                    >
                      {translate(LangEnum.MY_NETWORK_RECEIVED_LIST_ACCEPT)}
                    </Button>
                  </Grid>
                  <Grid size={12}>
                    <Button
                      fullWidth
                      variant="text"
                      color="secondary"
                      startIcon={<Close />}
                      onClick={() => handleAskReject('INVITE')}
                    >
                      {translate(LangEnum.MY_NETWORK_SUGGESTIONS_LIST_DECLINE)}
                    </Button>
                  </Grid>
                </Grid>
              </ButtonWithPopover>
            ) : (
              <Button variant="outlined" disabled fullWidth style={whiteBackgroundStyle}>
                {translate(LangEnum.MY_NETWORK_EXPLORE_RECEIVED)}
              </Button>
            )}
          </>
        )}

        {/* popup para editar vendedores e remover conexao */}
        {isStore && status === 'CONECTADO' && (
          <>
            {isAdmin ? (
              <ButtonWithPopover
                label={translate(LangEnum.MY_NETWORK_EXPLORE_CONNECTED)}
                fullWidth
                style={whiteBackgroundStyle}
              >
                <Grid container direction="column">
                  <Grid size={12}>
                    <Button
                      variant="text"
                      color="secondary"
                      startIcon={<Close />}
                      onClick={() =>
                        handleRemoveConnection({
                          connectionId: connection.connection?.id,
                          brandId: connection.brand?.id,
                        })
                      }
                    >
                      {translate(LangEnum.MY_NETWORK_EXPLORE_REMOVE_CONNECTION)}
                    </Button>
                  </Grid>
                </Grid>
              </ButtonWithPopover>
            ) : (
              <Button variant="outlined" color="primary" fullWidth style={whiteBackgroundStyle}>
                {translate(LangEnum.MY_NETWORK_EXPLORE_CONNECTED)}
              </Button>
            )}
          </>
        )}

        {/* acoes admin marca */}
        {!isStore && status === 'CONECTADO' && (
          <>
            {isAdminBrand ? (
              <ButtonWithPopover label={translate(LangEnum.MY_NETWORK_EXPLORE_CONNECTED)} fullWidth>
                <Grid container direction="column">
                  <Grid size={12}>
                    <Button
                      fullWidth
                      variant="text"
                      color="secondary"
                      startIcon={<Edit />}
                      onClick={handleOpenEditSalesRepModal}
                    >
                      {translate(LangEnum.MY_NETWORK_EDIT_SALES_REP)}
                    </Button>
                  </Grid>
                  <Grid size={12}>
                    <Button
                      fullWidth
                      variant="text"
                      color="secondary"
                      startIcon={<Close />}
                      onClick={() =>
                        handleRemoveConnection({
                          connectionId: connection.connection?.id,
                          brandId: connection.brand?.id,
                        })
                      }
                    >
                      {translate(LangEnum.MY_NETWORK_EXPLORE_REMOVE_CONNECTION)}
                    </Button>
                  </Grid>
                </Grid>
              </ButtonWithPopover>
            ) : (
              <Button variant="outlined" color="primary" fullWidth style={whiteBackgroundStyle}>
                {translate(LangEnum.MY_NETWORK_EXPLORE_CONNECTED)}
              </Button>
            )}
          </>
        )}

        {/* convite sugerido */}
        {/* vendedor vai poder remover sugestao */}
        {/* admin vai poder aceitar ou recusar */}
        {!isStore && status === 'SUGERIDO_VENDEDOR' && (
          <>
            {isAdminBrand && (
              <ButtonWithPopover
                label={translate(LangEnum.GLOBAL_SUGGESTED_SINGULAR)}
                fullWidth
                color="default"
              >
                <Grid container direction="column">
                  <Grid size={12}>
                    <Button
                      fullWidth
                      variant="text"
                      color="secondary"
                      startIcon={<Check />}
                      onClick={handleOpenSuggestionModal}
                    >
                      {translate(LangEnum.MY_NETWORK_RECEIVED_LIST_ACCEPT)}
                    </Button>
                  </Grid>
                  <Grid size={12}>
                    <Button
                      fullWidth
                      variant="text"
                      color="secondary"
                      startIcon={<Close />}
                      onClick={() => handleAskReject('SUGGESTION')}
                    >
                      {translate(LangEnum.MY_NETWORK_SUGGESTIONS_LIST_DECLINE)}
                    </Button>
                  </Grid>
                </Grid>
              </ButtonWithPopover>
            )}

            {isSeller && (
              <ButtonWithPopover
                label={translate(LangEnum.GLOBAL_SUGGESTED_SINGULAR)}
                fullWidth
                color="default"
              >
                <Grid container>
                  <Grid size={12}>
                    <Button
                      variant="text"
                      color="secondary"
                      startIcon={<Close />}
                      onClick={() => handleDeleteSuggestion(connection)}
                    >
                      {translate(LangEnum.MY_NETWORK_EXPLORE_REMOVE_SUGGESTION)}
                    </Button>
                  </Grid>
                </Grid>
              </ButtonWithPopover>
            )}
          </>
        )}

        {isRejectedAdmin && (
          <Button
            variant="text"
            color="secondary"
            disabled
            onClick={() => handleDeleteSuggestion(connection)}
          >
            {translate(LangEnum.MY_NETWORK_EXPLORE_WAITING_FOR_ANSWER)}
          </Button>
        )}

        {status === 'IGNORADO' && (
          <Grid container spacing={1}>
            {canUndoOwnRefusal && (
              <Grid size={12}>
                <Button variant="contained" color="primary" fullWidth onClick={handleAcceptInvite}>
                  {translate(LangEnum.MY_NETWORK_EXPLORE_ACCEPT_INVITATION)}
                </Button>
              </Grid>
            )}
            <Grid size={12}>
              <Button variant="outlined" onClick={() => handleRemoveInvite(connection)} fullWidth>
                {translate(LangEnum.MY_NETWORK_EXPLORE_REMOVE_INVITATION)}
              </Button>
            </Grid>
          </Grid>
        )}
      </>
    </>
  );
};

export default ConnectionActionButton;
